import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["modal", "image", "form"];
  static values = {
    attachmentId: String,
    cropperUrl: String,
  };

  connect() {
    console.log("AttachmentCropperController connected");
    console.log(
      "Attachment ID value:",
      this.attachmentIdValue,
      typeof this.attachmentIdValue
    );
    console.log(
      "Cropper URL value:",
      this.cropperUrlValue,
      typeof this.cropperUrlValue
    );
  }

  // Open cropper modal for existing attachment
  async openCropper(event) {
    event.preventDefault();
    console.log("AttachmentCropperController: openCropper called");
    console.log("Cropper URL:", this.cropperUrlValue);
    console.log("Attachment ID:", this.attachmentIdValue);
    console.log("Attachment ID type:", typeof this.attachmentIdValue);

    // Ensure we have valid values
    if (!this.cropperUrlValue) {
      console.error("No cropper URL provided");
      return;
    }

    if (!this.attachmentIdValue || this.attachmentIdValue.trim() === "") {
      console.error("Invalid attachment ID:", this.attachmentIdValue);
      return;
    }

    try {
      // Fetch the cropper content
      const response = await fetch(this.cropperUrlValue, {
        headers: {
          Accept: "text/html",
        },
      });

      if (response.ok) {
        const html = await response.text();
        console.log("Cropper HTML loaded successfully");
        this.showCropperModal(html);
      } else {
        console.error("Failed to load cropper:", response.status);
        const errorText = await response.text();
        console.error("Error response:", errorText);
      }
    } catch (error) {
      console.error("Error loading cropper:", error);
    }
  }

  showCropperModal(html) {
    // Always create a new modal container
    const modalContainer = document.createElement("div");
    modalContainer.id = "attachment-cropper-modal";
    modalContainer.className =
      "fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50";
    document.body.appendChild(modalContainer);

    // Store reference to modal container
    this.currentModalContainer = modalContainer;

    // Set up global close function
    window.closeCropperModal = () => this.hideCropperModal();

    // Set modal content
    modalContainer.innerHTML = html;

    // Show modal
    modalContainer.style.display = "block";
    document.body.classList.add("overflow-hidden");

    // Initialize the existing cropper functionality
    this.initializeExistingCropper(modalContainer);

    // Add close functionality
    this.addModalCloseHandlers(modalContainer);
  }

  initializeExistingCropper(modalContainer) {
    // Find the image and form in the modal
    const image = modalContainer.querySelector("img.cropper");
    const form = modalContainer.querySelector("form");

    console.log("Initializing cropper...");
    console.log("Image element:", image);
    console.log("Form element:", form);

    if (!image || !form) {
      console.error("Missing image or form elements");
      return;
    }

    // Import and initialize CropperJS
    import("cropperjs")
      .then(({ default: Cropper }) => {
        console.log("CropperJS imported successfully");

        const aspectRatio = parseFloat(image.dataset.aspectRatio);
        console.log("Aspect ratio:", aspectRatio);

        const cropper = new Cropper(image, {
          aspectRatio: undefined, // Allow free-form cropping
          viewMode: 0, // Free mode - no restrictions
          dragMode: "move",
          autoCropArea: 1,
          restore: false,
          guides: true,
          center: true,
          highlight: false,
          cropBoxMovable: true,
          cropBoxResizable: true,
          toggleDragModeOnDblclick: false,
          ready: () => {
            console.log("Cropper ready");
            // Store cropper instance
            this.currentCropper = cropper;

            // Restore existing crop coordinates if they exist
            this.restoreCropCoordinates(cropper, image);

            // If no existing crop and we have a suggested aspect ratio, set it
            if (!this.hasExistingCrop(image) && !isNaN(aspectRatio)) {
              this.setSuggestedAspectRatio(cropper, aspectRatio);
            }
          },
          crop: (event) => {
            // Update hidden form fields with crop coordinates
            this.updateCropCoordinates(form, event.detail);
          },
        });
      })
      .catch((error) => {
        console.error("Failed to import CropperJS:", error);
      });
  }

  hasExistingCrop(image) {
    const cropX = parseFloat(image.dataset.cropX);
    const cropY = parseFloat(image.dataset.cropY);
    const cropWidth = parseFloat(image.dataset.cropWidth);
    const cropHeight = parseFloat(image.dataset.cropHeight);

    return (
      !isNaN(cropX) && !isNaN(cropY) && !isNaN(cropWidth) && !isNaN(cropHeight)
    );
  }

  restoreCropCoordinates(cropper, image) {
    const cropX = parseFloat(image.dataset.cropX);
    const cropY = parseFloat(image.dataset.cropY);
    const cropWidth = parseFloat(image.dataset.cropWidth);
    const cropHeight = parseFloat(image.dataset.cropHeight);

    if (this.hasExistingCrop(image)) {
      cropper.setCropBoxData({
        left: cropX,
        top: cropY,
        width: cropWidth,
        height: cropHeight,
      });
    }
  }

  setSuggestedAspectRatio(cropper, aspectRatio) {
    // Set initial crop box dimensions based on suggested aspect ratio
    // but still allow free resizing afterwards
    if (!cropper) return;

    const containerData = cropper.getContainerData();

    // Calculate crop box dimensions that fit the container and match the aspect ratio
    let cropBoxWidth, cropBoxHeight;
    const containerAspect = containerData.width / containerData.height;

    if (aspectRatio > containerAspect) {
      // Aspect ratio is wider than container
      cropBoxWidth = containerData.width * 0.8;
      cropBoxHeight = cropBoxWidth / aspectRatio;
    } else {
      // Aspect ratio is taller than container
      cropBoxHeight = containerData.height * 0.8;
      cropBoxWidth = cropBoxHeight * aspectRatio;
    }

    // Center the crop box
    const left = (containerData.width - cropBoxWidth) / 2;
    const top = (containerData.height - cropBoxHeight) / 2;

    cropper.setCropBoxData({
      left: left,
      top: top,
      width: cropBoxWidth,
      height: cropBoxHeight,
    });
  }

  updateCropCoordinates(form, cropData) {
    const cropXField = form.querySelector('[name*="crop_x"]');
    const cropYField = form.querySelector('[name*="crop_y"]');
    const cropWidthField = form.querySelector('[name*="crop_width"]');
    const cropHeightField = form.querySelector('[name*="crop_height"]');

    if (cropXField) cropXField.value = Math.round(cropData.x);
    if (cropYField) cropYField.value = Math.round(cropData.y);
    if (cropWidthField) cropWidthField.value = Math.round(cropData.width);
    if (cropHeightField) cropHeightField.value = Math.round(cropData.height);
  }

  addModalCloseHandlers(modalContainer) {
    // Click outside modal to close
    modalContainer.addEventListener("click", (event) => {
      if (event.target === modalContainer) {
        this.hideCropperModal();
      }
    });

    // Escape key to close
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        this.hideCropperModal();
      }
    });

    // Form submit handler
    const form = modalContainer.querySelector("form");
    console.log("Form found:", form);
    if (form) {
      console.log("Adding form submit handler");
      form.addEventListener("submit", (event) =>
        this.handleCropSubmit(event, modalContainer)
      );
    } else {
      console.error("No form found in modal");
    }
  }

  async handleCropSubmit(event, modalContainer) {
    event.preventDefault();
    const form = event.target;
    const formData = new FormData(form);

    console.log("Form submission started");
    console.log("Form action:", form.action);
    console.log("Form data:", Object.fromEntries(formData));

    try {
      const response = await fetch(form.action, {
        method: "PATCH",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
      });

      console.log("Response status:", response.status);
      console.log("Response ok:", response.ok);

      if (response.ok) {
        this.showSuccessMessage("Image cropped successfully");
        this.hideCropperModal();
        // Refresh the page to show updated image
        window.location.reload();
      } else {
        console.error("Failed to crop image, status:", response.status);
        const errorText = await response.text();
        console.error("Error response:", errorText);
        this.showErrorMessage("Failed to crop image");
      }
    } catch (error) {
      console.error("Error cropping image:", error);
      this.showErrorMessage("Error cropping image");
    }
  }

  hideCropperModal() {
    // Use the stored reference to the modal container
    if (this.currentModalContainer) {
      // Destroy cropper instance
      if (this.currentCropper) {
        this.currentCropper.destroy();
        this.currentCropper = null;
      }

      // Remove modal from DOM
      this.currentModalContainer.remove();
      this.currentModalContainer = null;
      document.body.classList.remove("overflow-hidden");

      // Clean up global function
      delete window.closeCropperModal;
    }
  }

  showSuccessMessage(message) {
    // Simple success feedback
    console.log(message);
  }

  showErrorMessage(message) {
    console.error(message);
    alert(message);
  }
}
